/**
 * Hook for the WebSocket bridge that handles agent approval prompts.
 *
 * SRP: This hook ONLY handles the bridge connection and approval state.
 * No UI, no chat logic, no section logic.
 *
 * The agent uses request_user_input (e.g., for search approval) which
 * comes through the bridge as a "user_input" event. This hook surfaces
 * those as a pending approval that the UI can render and respond to.
 */

import { useEffect, useRef, useState } from "react";
import { getSessionToken } from "../api/client";
import { DocumentBridgeClient, type UserInputRequest } from "../services/documentBridge";

/**
 * Connection lifecycle of the WebSocket bridge.
 *   - "connecting": initial dial in flight, OR the client is mid-reconnect
 *     after a drop. Chat sends in this state should be queued, not errored.
 *   - "connected":  bridge is live. ``connectionId`` is set.
 *   - "disconnected": dial finished and we're not connected. Either the
 *     server is unreachable or the WS was rejected. Chat sends should
 *     surface a clear "bridge unreachable" error rather than a
 *     misleading "reopen the task pane" hint.
 */
export type BridgeStatus = "connecting" | "connected" | "disconnected";

export interface UseUserInputBridgeResult {
  connectionId: string | null;
  status: BridgeStatus;
  pendingApproval: UserInputRequest | null;
  submitApproval: (value: string) => void;
  rejectApproval: () => void;
}

export function useUserInputBridge(docId?: string): UseUserInputBridgeResult {
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [status, setStatus] = useState<BridgeStatus>("connecting");
  const [pendingApproval, setPendingApproval] = useState<UserInputRequest | null>(null);
  const bridgeRef = useRef<DocumentBridgeClient | null>(null);

  // Send doc_id context to the backend once both the bridge is connected
  // and the document ID is resolved. Either can arrive first (WS is fast,
  // doc ID comes from an async Office.js call), so we watch both.
  useEffect(() => {
    if (!docId || !connectionId || !bridgeRef.current) return;
    bridgeRef.current.sendContext(docId);
  }, [docId, connectionId]);

  useEffect(() => {
    const token = getSessionToken();
    const wsBase = `${
      globalThis.location.protocol === "https:" ? "wss:" : "ws:"
    }//${globalThis.location.host}/api/ws/document`;
    const wsUrl = token ? `${wsBase}?token=${encodeURIComponent(token)}` : wsBase;
    const bridge = new DocumentBridgeClient(wsUrl);
    bridgeRef.current = bridge;
    setStatus("connecting");

    bridge.onUserInputRequest((req) => {
      setPendingApproval(req);
    });

    // Subscribe to ALL connect/disconnect events — not just the first one.
    // The bridge client auto-reconnects after a drop with a NEW
    // connection_id. Without this, React state holds the stale id forever
    // and every chat call hits the backend's "bridge_not_found" guard.
    bridge.onConnect((connId) => {
      setConnectionId(connId);
      setStatus("connected");
    });
    bridge.onDisconnect(() => {
      setConnectionId(null);
      // Drop → "connecting" because the bridge client auto-reconnects.
      // The status only flips to "disconnected" on the initial dial
      // failure path below.
      setStatus("connecting");
      // A pending approval can't be answered over a dead socket, and
      // the server side fail-closes on timeout anyway (ApprovalGate
      // denies). Clearing here dismisses the stale card instead of
      // letting the writer click Approve into nothing.
      setPendingApproval(null);
    });

    bridge.connect().catch(() => {
      // Initial dial failed (server down / WS rejected). onConnect /
      // auto-reconnect handle eventual success — flip to
      // "disconnected" so the rail can show a clear unreachable
      // hint instead of an indefinite "connecting" spinner.
      setStatus("disconnected");
    });

    return () => {
      bridge.disconnect();
      bridgeRef.current = null;
    };
  }, []);

  const submitApproval = (value: string) => {
    if (pendingApproval && bridgeRef.current) {
      bridgeRef.current.submitUserInput(pendingApproval.id, value);
      setPendingApproval(null);
    }
  };

  const rejectApproval = () => {
    if (pendingApproval && bridgeRef.current) {
      bridgeRef.current.submitUserInput(pendingApproval.id, "");
      setPendingApproval(null);
    }
  };

  return {
    connectionId,
    status,
    pendingApproval,
    submitApproval,
    rejectApproval,
  };
}
